/**
 * OWL2 Literal Types
 *
 * Represents RDF literals as used in OWL2.
 * Supports typed literals (with datatype IRI) and plain literals (with optional language tag).
 */

import type { IRI } from './IRI.ts';

// =============================================================================
// Type Definitions
// =============================================================================

/**
 * Typed literal - a value with an explicit datatype IRI.
 * Example: "42"^^xsd:integer
 */
export interface TypedLiteral {
    readonly kind: 'TypedLiteral';
    readonly lexicalForm: string;
    readonly datatypeIRI: IRI;
}

/**
 * Plain literal - a string value with optional language tag.
 * Example: "Hello"@en
 */
export interface PlainLiteral {
    readonly kind: 'PlainLiteral';
    readonly lexicalForm: string;
    readonly languageTag?: string;
}

/**
 * Discriminated union of literal types.
 * The 'kind' field distinguishes between typed and plain literals.
 */
export type Literal = TypedLiteral | PlainLiteral;

// =============================================================================
// Factory Functions
// =============================================================================

/**
 * Create a typed literal with explicit datatype.
 */
export function typedLiteral(lexicalForm: string, datatypeIRI: IRI): TypedLiteral {
    return { kind: 'TypedLiteral', lexicalForm, datatypeIRI };
}

/**
 * Create a plain literal with optional language tag.
 */
export function plainLiteral(lexicalForm: string, languageTag?: string): PlainLiteral {
    return {
        kind: 'PlainLiteral',
        lexicalForm,
        ...(languageTag && { languageTag })
    };
}

// =============================================================================
// Type Guards
// =============================================================================

/**
 * Check if a literal is a typed literal.
 */
export function isTypedLiteral(literal: Literal): literal is TypedLiteral {
    return literal.kind === 'TypedLiteral';
}

/**
 * Check if a literal is a plain literal.
 */
export function isPlainLiteral(literal: Literal): literal is PlainLiteral {
    return literal.kind === 'PlainLiteral';
}

/**
 * Check if a plain literal has a language tag.
 */
export function hasLanguageTag(literal: Literal): literal is PlainLiteral & { languageTag: string } {
    return literal.kind === 'PlainLiteral' && literal.languageTag !== undefined;
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get the lexical form (string value) of a literal.
 */
export function getLexicalForm(literal: Literal): string {
    return literal.lexicalForm;
}

/**
 * Convert a literal to a human-readable string representation.
 */
export function literalToString(literal: Literal): string {
    switch (literal.kind) {
        case 'TypedLiteral': {
            const datatypeStr = literal.datatypeIRI.kind === 'FullIRI'
                ? literal.datatypeIRI.value
                : `${literal.datatypeIRI.prefix}:${literal.datatypeIRI.localName}`;
            return `"${literal.lexicalForm}"^^<${datatypeStr}>`;
        }
        case 'PlainLiteral':
            if (literal.languageTag) {
                return `"${literal.lexicalForm}"@${literal.languageTag}`;
            }
            return `"${literal.lexicalForm}"`;
    }
}

/**
 * Compare two literals for equality.
 */
export function literalEquals(a: Literal, b: Literal): boolean {
    if (a.kind !== b.kind) return false;
    if (a.lexicalForm !== b.lexicalForm) return false;

    if (a.kind === 'TypedLiteral' && b.kind === 'TypedLiteral') {
        // Compare datatype IRIs
        if (a.datatypeIRI.kind !== b.datatypeIRI.kind) return false;
        if (a.datatypeIRI.kind === 'FullIRI' && b.datatypeIRI.kind === 'FullIRI') {
            return a.datatypeIRI.value === b.datatypeIRI.value;
        }
        if (a.datatypeIRI.kind === 'AbbreviatedIRI' && b.datatypeIRI.kind === 'AbbreviatedIRI') {
            return a.datatypeIRI.prefix === b.datatypeIRI.prefix &&
                   a.datatypeIRI.localName === b.datatypeIRI.localName;
        }
    }

    if (a.kind === 'PlainLiteral' && b.kind === 'PlainLiteral') {
        return a.languageTag === b.languageTag;
    }

    return false;
}
